import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError, dateFmt, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, Th, Td, SortTh, useSortable } from "@/components/common";
import { ListFilter, useListFilter } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PackagePlus, Plus, Trash2, Undo2 } from "lucide-react";
import { ReceiptLineExtras, ReversalDialog } from "@/components/receiving/ReceiptExtras";
import InputTaxDialog, { InputTaxBadge } from "@/components/receiving/InputTaxDialog";

const NONE = "__none__";

export default function Receiving() {
  const { user } = useAuth();
  const { t, tp } = useI18n();
  const canEdit = can(user, "receiving:edit");
  const [rows, setRows] = useState(null);
  const [variants, setVariants] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [packaging, setPackaging] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locEnabled, setLocEnabled] = useState(false);
  const [reverseFor, setReverseFor] = useState(null);
  const [taxFor, setTaxFor] = useState(null);   // Eingangsbeleg, dessen Vorsteuer erfasst wird
  const [taxStatus, setTaxStatus] = useState({}); // receipt_id -> COMPLETE|PARTIAL|MISSING
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ supplier_id: "", reference: "", expected_date: "", bookNow: true, packaging_id: NONE, packaging_qty: 1, transport_cost: "", transport_tax: "STANDARD", customs_cost: "", customs_tax: "ZERO_RATE", lines: [] });
  const TAX_CODES = ["STANDARD", "REDUCED", "ZERO_RATE"];

  const ACC = useMemo(() => ({
    created_at: (r) => r.created_at || "",
    reference: (r) => r.reference || "",
    lines: (r) => r.lines.length,
    qty: (r) => r.lines.reduce((a, l) => a + l.quantity, 0),
    status: (r) => r.status || "",
  }), []);
  const filter = useListFilter(rows, { fields: ["reference", "status"], dateField: "created_at", statusField: "status", storageKey: "receiving" });
  const { sorted, sortKey, sortDir, toggle } = useSortable(filter.filtered, ACC);
  const sp = (k) => ({ sortId: k, active: sortKey === k, dir: sortDir, onSort: toggle });

  const receive = async (r) => {
    try { await api.post(`/purchase-receipts/${r.id}/receive`); toast.success(t("st.postedToStock")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(() => {
    api.get("/purchase-receipts").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    api.get("/packaging").then((r) => setPackaging(r.data)).catch(() => {});
    api.get("/storage-locations").then((r) => { setLocations(r.data.locations || []); setLocEnabled(!!r.data.management_enabled); }).catch(() => {});
    // Vorsteuerstatus je Eingangsbeleg (Abschnitt B) — nur lesend, keine Periodenauswertung.
    api.get("/input-tax/documents", { params: { kind: "goods" } })
      .then((r) => setTaxStatus(Object.fromEntries(r.data.documents.map((d) => [d.id, d.status]))))
      .catch(() => setTaxStatus({}));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pmap = Object.fromEntries(packaging.map((p) => [p.id, p]));
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, { _k: crypto.randomUUID(), variant_id: variants[0]?.id || "", quantity: 1, unit_cost: "", cert_cost: "", cert_tax: "REDUCED" }] }));
  const upd = (i, k, v) => { const l = [...f.lines]; l[i] = { ...l[i], [k]: v }; setF({ ...f, lines: l }); };
  const rm = (i) => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) });

  // Landed-Cost-Vorschau (identische Logik wie Backend _apply_landed_costs): Transport+Zoll werden
  // nach Warenwert verteilt, Zertifizierung je Position; Gesamt-Netto-EK = Waren + Nebenkosten.
  const landed = useMemo(() => {
    const ls = f.lines.map((l) => ({ base: Number(l.unit_cost) || 0, qty: Number(l.quantity) || 0, cert: Number(l.cert_cost) || 0 }));
    const vals = ls.map((x) => x.base * x.qty);
    const totalVal = vals.reduce((a, b) => a + b, 0);
    const ship = (Number(f.transport_cost) || 0) + (Number(f.customs_cost) || 0);
    const perLine = ls.map((x, i) => {
      const factor = totalVal > 0 ? vals[i] / totalVal : (ls.length ? 1 / ls.length : 0);
      const total = x.base * x.qty + ship * factor + x.cert;
      return { landedUnit: x.qty > 0 ? total / x.qty : 0 };
    });
    const certSum = ls.reduce((a, x) => a + x.cert, 0);
    return { perLine, goods: totalVal, extra: ship + certSum, totalNet: totalVal + ship + certSum };
  }, [f.lines, f.transport_cost, f.customs_cost]);

  const dims = (p) => [p?.length_cm, p?.width_cm, p?.height_cm].every((x) => x != null && x !== "")
    ? `${p.length_cm}×${p.width_cm}×${p.height_cm} cm` : "";

  const save = async () => {
    try {
      const hasPack = f.packaging_id && f.packaging_id !== NONE;
      await api.post("/purchase-receipts", {
        supplier_id: f.supplier_id || suppliers[0]?.id, warehouse_id: warehouses[0]?.id,
        reference: f.reference, expected_date: f.expected_date || null,
        status: f.bookNow ? "eingegangen" : "bestellt",
        packaging_id: hasPack ? f.packaging_id : null,
        packaging_qty: hasPack ? (Number(f.packaging_qty) || 0) : 0,
        transport_cost_net: Number(f.transport_cost) || 0, transport_tax_code: f.transport_tax,
        customs_cost_net: Number(f.customs_cost) || 0, customs_tax_code: f.customs_tax,
        lines: f.lines.map((l) => ({ variant_id: l.variant_id, quantity: Number(l.quantity) || 0, unit_cost: Number(l.unit_cost) || 0,
          cert_cost_net: Number(l.cert_cost) || 0, cert_tax_code: l.cert_tax || null,
          lot_number: l.lot_number || "", best_before_date: l.best_before_date || null,
          storage_location_id: l.storage_location_id || null, quality: l.quality || "good" })) });
      toast.success(f.bookNow ? t("rec.booked") : t("rec.reorderCreated"));
      setOpen(false); setF({ supplier_id: "", reference: "", expected_date: "", bookNow: true, packaging_id: NONE, packaging_qty: 1, transport_cost: "", transport_tax: "STANDARD", customs_cost: "", customs_tax: "ZERO_RATE", lines: [] }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title={t("rec.title")} subtitle={t("rec.subtitle")}
        actions={canEdit && <Button data-testid="new-receipt-btn" onClick={() => { setOpen(true); if (!f.lines.length) addLine(); }}><Plus className="h-4 w-4 mr-1.5" /> {t("rec.title")}</Button>} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="receipts-empty" icon={PackagePlus} title={t("rec.emptyTitle")} hint={t("rec.emptyHint")} /> : (
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <div className="border-b border-border p-3">
              <ListFilter filter={filter} testId="receipts" withDates withStatus placeholder={t("rec.searchPh")}
                statusLabel={(s) => (s === "bestellt" ? t("st.ordered") : t("st.received"))}
                exportName="wareneingang" exportRows={sorted} exportColumns={[
                  { label: t("common.date"), value: (r) => dateFmt(r.created_at) },
                  { label: t("common.reference"), value: (r) => r.reference || "" },
                  { label: t("common.positions"), value: (r) => r.lines.length },
                  { label: t("rec.unitsTotal"), value: (r) => r.lines.reduce((a, l) => a + l.quantity, 0) },
                  { label: t("rec.carton"), value: (r) => (r.packaging_id && pmap[r.packaging_id] ? `${pmap[r.packaging_id].name} · ${r.packaging_qty || 0}` : "") },
                  { label: t("common.status"), value: (r) => (r.status === "bestellt" ? t("st.ordered") : t("st.received")) },
                  { label: t("wg.reverse"), value: (r) => (r.reversal_status ? t(`wg.${r.reversal_status === "storniert" ? "reversed" : "partlyReversed"}`) : "") },
                ]} />
            </div>
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <SortTh {...sp("created_at")}>{t("common.date")}</SortTh>
                <SortTh {...sp("reference")}>{t("common.reference")}</SortTh>
                <SortTh right {...sp("lines")}>{t("common.positions")}</SortTh>
                <SortTh right {...sp("qty")}>{t("rec.unitsTotal")}</SortTh>
                <Th>{t("rec.carton")}</Th>
                <Th>{t("itx.title")}</Th>
                <SortTh {...sp("status")}>{t("common.status")}</SortTh></tr></thead>
              <tbody data-testid="receipts-table">
                {sorted.length === 0 && (
                  <tr><Td colSpan={7} className="text-muted-foreground" data-testid="receipts-no-match">{t("common.noMatchFor", { q: filter.q })}</Td></tr>
                )}
                {sorted.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40"><Td>{dateFmt(r.created_at)}</Td>
                    <Td>{r.reference || "—"}
                      {r.receipt_type === "initial_stock" && <span className="ml-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid={`receipt-initial-${r.id}`}>{t("tr.initialStockLabel")}</span>}</Td>
                    <Td right mono>{r.lines.length}</Td><Td right mono>{r.lines.reduce((a, l) => a + l.quantity, 0)}</Td>
                    <Td>{r.packaging_id && pmap[r.packaging_id] ? `${pmap[r.packaging_id].name} · ${r.packaging_qty || 0}×` : "—"}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-2">
                        <InputTaxBadge status={taxStatus[r.id] || "MISSING"} testId={`receipt-input-tax-${r.id}`} />
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => setTaxFor(r)}
                            data-testid={`capture-input-tax-${r.id}`}>{t("itx.capture")}</Button>
                        )}
                      </span>
                    </Td>
                    <Td>{r.status === "bestellt"
                      ? <span className="inline-flex items-center gap-2"><span className="rounded-full bg-chart-3/20 text-chart-3 text-xs px-2 py-0.5">{t("st.ordered")}</span>{canEdit && <Button size="sm" variant="outline" onClick={() => receive(r)} data-testid={`receive-${r.reference || r.id.slice(0,4)}`}>{t("st.postToStock")}</Button>}</span>
                      : <span className="inline-flex items-center gap-2">
                          <span className="rounded-full bg-primary/15 text-primary text-xs px-2 py-0.5">{t("st.received")}</span>
                          {r.reversal_status === "storniert" && <span className="rounded-full bg-destructive/15 text-destructive text-xs px-2 py-0.5" data-testid={`receipt-reversed-${r.id}`}>{t("wg.reversed")}</span>}
                          {r.reversal_status === "teilstorniert" && <span className="rounded-full bg-chart-3/20 text-chart-3 text-xs px-2 py-0.5" data-testid={`receipt-partly-reversed-${r.id}`}>{t("wg.partlyReversed")}</span>}
                          {canEdit && r.reversal_status !== "storniert" && (
                            <Button size="sm" variant="outline" onClick={() => setReverseFor(r)} data-testid={`reverse-receipt-${r.id}`}>
                              <Undo2 className="mr-1 h-3.5 w-3.5" /> {t("wg.reverse")}
                            </Button>
                          )}
                        </span>}</Td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("rec.newTitle")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("rec.refLabel")}</Label><Input data-testid="receipt-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("common.supplier")}</Label>
              <Select value={f.supplier_id || suppliers[0]?.id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder={t("common.supplier")} /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2"><Label>{t("common.positions")}</Label><Button size="sm" variant="outline" onClick={addLine} data-testid="add-receipt-line"><Plus className="h-3.5 w-3.5 mr-1" /> {t("common.position")}</Button></div>
          <div className="space-y-2">
            {f.lines.map((l, i) => (
              <div key={l._k || i} className="flex flex-wrap items-center gap-2">
                <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
                <Select value={l.variant_id} onValueChange={(v) => upd(i, "variant_id", v)}>
                  <SelectTrigger className="w-full min-w-0 flex-1 sm:w-auto [&>span]:truncate" data-testid={`receipt-variant-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.product_name} · {v.sku}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" className="w-20 shrink-0" placeholder={t("common.quantity")} value={l.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`receipt-qty-${i}`} />
                <Input type="number" className="w-24 shrink-0" placeholder={t("rec.netEk", "Netto-EK")} value={l.unit_cost} onChange={(e) => upd(i, "unit_cost", e.target.value)} data-testid={`receipt-cost-${i}`} />
                <Input type="number" className="w-24 shrink-0" placeholder={t("rec.certCost", "Zertifiz. netto")} value={l.cert_cost || ""} onChange={(e) => upd(i, "cert_cost", e.target.value)} data-testid={`receipt-cert-${i}`} />
                <Select value={String(l.cert_tax ?? "REDUCED")} onValueChange={(v) => upd(i, "cert_tax", v)}>
                  <SelectTrigger className="w-[112px] shrink-0" data-testid={`receipt-cert-tax-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{TAX_CODES.map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}</SelectContent>
                </Select>
                <span className="w-24 shrink-0 text-right text-xs text-muted-foreground" title={t("rec.landedEk", "Landed-EK/Stück")} data-testid={`receipt-landed-${i}`}>{eur(landed.perLine[i]?.landedUnit || 0)}</span>
                <Button size="icon" variant="ghost" className="shrink-0" onClick={() => rm(i)} data-testid={`receipt-line-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <ReceiptLineExtras line={l} index={i} locations={locations} locationsEnabled={locEnabled}
                  warehouseId={warehouses[0]?.id}
                  variant={variants.find((v) => v.id === l.variant_id)}
                  onChange={(k, v) => upd(i, k, v)} />
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-md border border-border p-3" data-testid="receipt-shipment-costs">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("rec.shipmentCosts", "Nebenkosten der Lieferung (Transport & Zoll)")}</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div><Label className="text-xs">{t("rec.transportCost", "Transport netto")}</Label><Input type="number" className="mt-1 h-9" value={f.transport_cost} onChange={(e) => setF({ ...f, transport_cost: e.target.value })} data-testid="receipt-transport" /></div>
              <div><Label className="text-xs">{t("rec.vat", "USt")}</Label><Select value={String(f.transport_tax)} onValueChange={(v) => setF({ ...f, transport_tax: v })}><SelectTrigger className="mt-1 h-9" data-testid="receipt-transport-tax"><SelectValue /></SelectTrigger><SelectContent>{TAX_CODES.map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">{t("rec.customsCost", "Zoll netto")}</Label><Input type="number" className="mt-1 h-9" value={f.customs_cost} onChange={(e) => setF({ ...f, customs_cost: e.target.value })} data-testid="receipt-customs" /></div>
              <div><Label className="text-xs">{t("rec.vat", "USt")}</Label><Select value={String(f.customs_tax)} onValueChange={(v) => setF({ ...f, customs_tax: v })}><SelectTrigger className="mt-1 h-9" data-testid="receipt-customs-tax"><SelectValue /></SelectTrigger><SelectContent>{TAX_CODES.map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="mt-2 flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm" data-testid="receipt-cost-summary">
              <span className="text-muted-foreground">{t("rec.goodsNet", "Waren-Netto")}: <span className="font-medium text-foreground">{eur(landed.goods)}</span></span>
              <span className="text-muted-foreground">{t("rec.extraNet", "Nebenkosten")}: <span className="font-medium text-foreground">{eur(landed.extra)}</span></span>
              <span className="font-semibold text-foreground" data-testid="receipt-total-net-ek">{t("rec.totalNetEk", "Gesamt-Netto-EK")}: {eur(landed.totalNet)}</span>
            </div>
          </div>

          <div className="mt-3 rounded-md border border-border p-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("rec.cartonOptional")}</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              <div className="col-span-2">
                <Select value={f.packaging_id} onValueChange={(v) => setF({ ...f, packaging_id: v })}>
                  <SelectTrigger data-testid="receipt-packaging"><SelectValue placeholder={t("rec.noCarton")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t("rec.noCarton")}</SelectItem>
                    {packaging.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{dims(p) ? ` · ${dims(p)}` : ""} · {t("common.stock")} {p.stock ?? 0}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input type="number" min="1" className="w-full" placeholder={t("rec.count")} value={f.packaging_qty}
                disabled={!f.packaging_id || f.packaging_id === NONE}
                onChange={(e) => setF({ ...f, packaging_qty: e.target.value })} data-testid="receipt-packaging-qty" />
            </div>
            {f.packaging_id && f.packaging_id !== NONE && (
              <p className="text-xs text-muted-foreground mt-1.5">{tp("plural.cartonHint", Number(f.packaging_qty) || 0)}</p>
            )}
          </div>

          <DialogFooter><Button data-testid="save-receipt-btn" onClick={save} disabled={!f.lines.length}>{t("common.book")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ReversalDialog receipt={reverseFor} onClose={() => setReverseFor(null)}
        onDone={() => { setReverseFor(null); load(); }} />
      <InputTaxDialog kind="goods" receiptId={taxFor?.id} open={!!taxFor}
        onClose={() => setTaxFor(null)} onSaved={() => load()} />
    </div>
  );
}
